import React, { useState, useEffect } from 'react';
import styles from './Checklist.module.scss';

import {
	Paper,
	Typography,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	Checkbox,
} from '@mui/material';
// TemplatePage import removed (not used in this file)
import { useHistory } from 'react-router-dom';
import ToggleViews from './ToggleView/ToggleViews';
import CompletedTemplatePage from 'module/settings/settingsGameMasterTabs/CompletedTemplatePage/CompletedTemplatePage';
import ApprovalTemplatePage from 'module/settings/settingsGameMasterTabs/ApprovalTemplatePage/ApprovalTemplatePage';

import PastDueLogo from '../../assets/images/pastDueLogo.png';
import useHeaderStore from 'store/useHeaderStore';
import {
	getScheduledChecklistTemplates,
	getCategorizedChecklistScheduleEvents,
	getLatestChecklistTemplateByUid,
	getOnTimeStats,
	getChecklistScheduleById,
	deactivateChecklistSchedule,
	getChecklistTemplateById,
} from 'core/API/checklist';
import { useDocumentContext } from 'module/documents/context';
import usePermissionCheck from 'core/hooks/usePermissionCheck';
import { formatDateWith12HourTime, formatDateTime12Hour } from 'shared/utils/dateUtils';

// Helper function to format date as mm/dd/yyyy
const formatDate = (dateString: string) => {
	return new Date(dateString).toLocaleDateString('en-US', {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
	});
};

// Helper function to format date and time as mm/dd/yyyy HH:MM:SS AM/PM
const formatDateTime = (dateString: string) => {
	return formatDateWith12HourTime(dateString);
};

// Helper function to format date from API format (YYYY-MM-DD) to mm/dd/yyyy
const formatDateFromAPI = (dateString: string) => {
	if (!dateString) return '';

	// Handle YYYY-MM-DD format
	const parts = dateString.split('-');
	if (parts.length === 3) {
		const [year, month, day] = parts;
		return `${month}/${day}/${year}`;
	}

	// Fallback to original formatting
	return formatDate(dateString);
};

// Helper function to format date and time from API formatted strings (mm/dd/yyyy HH:MM:SS AM/PM)
const formatDateTimeFromAPI = (dateFormatted: string, timeFormatted: string) => {
	return formatDateTime12Hour(dateFormatted, timeFormatted);
};

// Interfaces
interface ChecklistItem {
	id: string;
	section: string;
	duty: string;
	instructions: string;
	answerType: string;
	correctAnswer: string;
	comment: string;
	submittedAnswer: string;
	picLink: string;
	isEditing: boolean;
	isSaved: boolean;
	isSelected: boolean;
	time: string;
	isValid: {
		section: boolean;
		duty: boolean;
		instructions: boolean;
		answerType: boolean;
		correctAnswer: boolean;
		comment: boolean;
		picLink: boolean;
	};
	errors: Record<string, string>;
}

interface Template {
	id: number;
	version: string;
	name: string;
	category: string;
	owner: string;
	frequency: string;
	frequencyObject?: { id: number; label: string };
	assigned: string;
	active: boolean;
	signoffRequired?: boolean;
	templateId?: number;
	templateUid?: string;
}

// New interface for scheduled checklist templates
// API Response Structure from /checklist_scheduled_templates?page=1&workplace=1
interface ScheduledChecklistTemplate {
	id: number;
	author: {
		id: number;
		firstName: string;
		middleName: string;
		lastName: string;
		email: string;
	};
	workplace: {
		id: number;
		name: string;
	};
	organization?: {
		id: number;
		name: string;
	} | null;
	label: string;
	category: {
		id: number;
		label: string;
	};
	frequency: {
		id: number;
		label: string;
	};
	templateOwner: string;
	signoffRequired: boolean;
	version: number;
	tasks: Array<{
		id: number;
		section: string;
		duty: string;
		instructions: string;
		answerType: string;
		expectedAnswer: string;
		listOrder: number;
	}>;
	isScheduled: boolean | string | number;
	templateUid?: string;
	type?: string; // Added type field for owner display
	checklistScheduleId?: number; // Added field for schedule ID
	// Additional fields for version checking
	latestVersion?: number;
	versionChecked?: boolean;
	versionError?: string;
}

// Interface for categorized checklist schedule events
// API Response Structure from /checklist_schedule_event/categorized?date=2025-09-03&workplace=1
interface ChecklistScheduleEvent {
	id: number;
	checklistSchedule: {
		id: number;
		templateVersion: number;
		assignmentType: string;
		assignee: {
			id: number;
			firstName: string;
			middleName: string;
			lastName: string;
			email: string;
		} | null;
		group: {
			id: number;
			label: string;
		} | null;
	};
	checklistTemplate: {
		id: number;
		label: string;
		signoffRequired: boolean;
		version: number;
		isScheduled: boolean | string | number;
		type?: string; // Added type field for owner display (workplace or organization)
		category?: {
			id: number;
			label: string;
		};
		frequency?: {
			id: number;
			label: string;
		};
		tasks?: Array<{
			id: number;
			section: string;
			duty: string;
			instructions: string;
			answerType: string;
			expectedAnswer: string;
			listOrder: number;
		}>;
	};
	workplace: {
		id: number;
		name: string;
	};
	organization?: {
		id: number;
		name: string;
	} | null;
	scheduledDate: string;
	scheduledTime: string;
	status: string;
	completedBy: any;
	completedAt: any;
	notes: any;
	signoffRequired: boolean;
	approved: boolean;
	approvedBy: any;
	isPastDueFlag?: boolean | null;
	scheduledDateFormatted?: string;
	scheduledTimeFormatted?: string;
}

interface CategorizedChecklistEvents {
	completed: ChecklistScheduleEvent[];
	myOutstanding: ChecklistScheduleEvent[];
	waitingForApproval: ChecklistScheduleEvent[];
	pastDue: ChecklistScheduleEvent[];
}

// TemplatePageProps removed (not used here)

interface ViewProps {
	onBack: () => void;
	headerData?: {
		version: string;
		checklistName: string;
		category: string;
		owner: string;
		frequency: string;
		date: string;
		signoffRequired: boolean;
	}[];
	selectedItem?: ChecklistScheduleEvent;
}

// View Components
const CompletedChecklistView: React.FC<ViewProps> = ({ onBack, selectedItem }) => {
	return (
		<Paper
			elevation={3}
			className={styles.containerr}
			style={{ padding: '20px', backgroundColor: 'whitesmoke' }}
		>
			<Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
				<Button
					variant="outlined"
					onClick={onBack}
					className={styles.button}
					style={{ backgroundColor: 'rgb(68, 99, 136)', padding: '5px 12px', color: 'white' }}
				>
					Back
				</Button>
			</Box>
			<CompletedTemplatePage
				accessibility="completed"
				onBackClick={onBack}
				initialData={[]}
				selectedItem={selectedItem}
			/>
		</Paper>
	);
};

interface ApprovalChecklistViewProps extends ViewProps {
	onRefresh?: () => void;
}

const ApprovalChecklistView: React.FC<ApprovalChecklistViewProps> = ({ onBack, selectedItem, onRefresh }) => {
	return (
		<Paper
			elevation={3}
			className={styles.containerr}
			style={{ padding: '20px', backgroundColor: 'whitesmoke' }}
		>
			<Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
				<Button
					variant="outlined"
					onClick={onBack}
					className={styles.button}
					style={{ backgroundColor: 'rgb(68, 99, 136)', padding: '5px 12px', color: 'white' }}
				>
					Back
				</Button>
			</Box>
			<ApprovalTemplatePage onBackClick={onBack} initialData={[]} selectedItem={selectedItem} onApprovalSuccess={onRefresh} />
		</Paper>
	);
};

const StatsChecklistView: React.FC<ViewProps> = ({ onBack }) => {
	const [stats, setStats] = useState<Array<{ period: string; onTime: number; signOff: number }>>(
		[]
	);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		const loadStats = async () => {
			setLoading(true);
			try {
				const data = await getOnTimeStats();
				setStats(data);
			} catch (e) {
				setStats([]);
			} finally {
				setLoading(false);
			}
		};
		loadStats();
	}, []);

	const getValue = (period: string, key: 'onTime' | 'signOff') => {
		const found = stats.find((s) => s.period === period);
		if (!found) return '0%';
		const value = found[key];
		const formatted = typeof value === 'number' ? value.toFixed(1) + '%' : String(value);
		return formatted;
	};

	return (
		<Paper
			elevation={3}
			className={styles.containerr}
			style={{ padding: '20px', backgroundColor: 'whitesmoke' }}
		>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Typography variant="h5">Stats</Typography>
				<Button
					variant="outlined"
					onClick={onBack}
					className={styles.button}
					style={{ backgroundColor: 'rgb(68, 99, 136)', padding: '5px 12px', color: 'white' }}
				>
					Back
				</Button>
			</Box>
			<TableContainer component={Paper} sx={{ maxHeight: '300px', overflowY: 'auto' }}>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
								Checklist Name
							</TableCell>
							<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
								Assigned
							</TableCell>
							<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
								Completed
							</TableCell>
							<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
								Checklist Time
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={4} align="center">
									<CircularProgress size={20} color="inherit" />
								</TableCell>
							</TableRow>
						) : (
							<>
								<TableRow>
									<TableCell>WTD</TableCell>
									<TableCell>{getValue('WTD', 'onTime')}</TableCell>
									<TableCell>{getValue('WTD', 'signOff')}</TableCell>
									<TableCell>-</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>PTD</TableCell>
									<TableCell>{getValue('PTD', 'onTime')}</TableCell>
									<TableCell>{getValue('PTD', 'signOff')}</TableCell>
									<TableCell>-</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>YTD</TableCell>
									<TableCell>{getValue('YTD', 'onTime')}</TableCell>
									<TableCell>{getValue('YTD', 'signOff')}</TableCell>
									<TableCell>-</TableCell>
								</TableRow>
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);
};

// Outstanding Checklist View Component using the same layout as CompletedTemplatePage
const OutstandingChecklistView: React.FC<ViewProps> = ({ onBack: _onBack, selectedItem }) => {
	const [items, setItems] = useState<ChecklistItem[]>([]);
	const [loading, setLoading] = useState(false);

	const columns = [
		{ id: 'section', header: 'Section' },
		{ id: 'duty', header: 'Duty' },
		{ id: 'instructions', header: 'Instructions' },
		{ id: 'answerType', header: 'Answer' },
		{ id: 'correctAnswer', header: 'Correct' },
		{ id: 'submittedAnswer', header: 'Submitted' },
		{ id: 'picLink', header: 'Picture' },
	];

	// Fetch checklist template data when component mounts or selectedItem changes
	useEffect(() => {
		if (selectedItem) {
			fetchChecklistTemplateData();
		}
		// fetchChecklistTemplateData is stable within this component and intentionally
		// omitted from deps to avoid re-creating it on every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedItem]);

	const fetchChecklistTemplateData = async () => {
		if (!selectedItem) return;

		setLoading(true);
		try {
			// Convert the tasks from the selected item to ChecklistItem format
			const checklistItems: ChecklistItem[] =
				selectedItem.checklistTemplate.tasks?.map((task) => ({
					id: task.id.toString(),
					section: task.section || '',
					duty: task.duty || '',
					instructions: task.instructions || '',
					answerType: task.answerType || '',
					correctAnswer: task.expectedAnswer || '',
					submittedAnswer: '', // Empty for outstanding items
					comment: '',
					picLink: '',
					isEditing: false,
					isSaved: false,
					isSelected: false,
					time: '',
					isValid: {
						section: true,
						duty: true,
						instructions: true,
						answerType: true,
						correctAnswer: true,
						comment: true,
						picLink: true,
					},
					errors: {},
				})) || [];

			setItems(checklistItems);
		} catch (error) {
			console.error('Error processing checklist template data:', error);
			setItems([]);
		} finally {
			setLoading(false);
		}
	};

	const headerData = selectedItem
		? {
				version: selectedItem.checklistTemplate?.version || 1,
				checklistName: selectedItem.checklistTemplate?.label || 'Unknown Template',
				category: (selectedItem.checklistTemplate as any)?.category?.label || 'Unknown Category',
				owner:
					selectedItem.checklistTemplate?.type === 'workplace'
						? 'Workplace'
						: selectedItem.checklistTemplate?.type === 'organization'
							? 'Organization'
							: 'Unknown',
				frequency: (selectedItem.checklistTemplate as any)?.frequency?.label || 'Unknown Frequency',
				date: formatDate(selectedItem.scheduledDate),
			}
		: {
				version: 1,
				checklistName: 'Outstanding Checklist',
				category: 'Unknown Category',
				owner: 'Unknown',
				frequency: 'Unknown Frequency',
				date: formatDate(new Date().toISOString()),
			};

	return (
		<Paper elevation={2} sx={{ p: 0, overflow: 'hidden' }}>
			<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
				<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
					<Typography variant="body1">
						<strong>Version:</strong> {headerData.version}
					</Typography>
					<Typography variant="body1">
						<strong>Checklist Name:</strong> {headerData.checklistName}
					</Typography>
					<Typography variant="body1">
						<strong>Category:</strong> {headerData.category}
					</Typography>
					<Typography variant="body1">
						<strong>Owner:</strong> {headerData.owner}
					</Typography>
					<Typography variant="body1">
						<strong>Frequency:</strong> {headerData.frequency}
					</Typography>
					<Typography variant="body1">
						<strong>Date:</strong> {headerData.date}
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Checkbox
							checked={selectedItem?.checklistTemplate?.signoffRequired || false}
							disabled
							color="primary"
							size="small"
						/>
						<Typography variant="body1">
							<strong>Sign Off</strong>
						</Typography>
					</Box>
				</Box>
			</Box>

			{loading ? (
				<Box sx={{ p: 4, textAlign: 'center' }}>
					<CircularProgress size={24} className={styles.loadingSpinner} />
				</Box>
			) : (
				<TableContainer sx={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								{columns.map((column) => (
									<TableCell
										key={column.id}
										sx={{
											textAlign: column.id === 'section' ? 'left' : 'center',
											paddingLeft: column.id === 'section' ? '8px' : '1px',
										}}
									>
										{column.header}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{items.map((item) => (
								<React.Fragment key={item.id}>
									<TableRow>
										{columns.map((column) => (
											<TableCell
												key={`${item.id}-${column.id}`}
												sx={{
													p: 1,
													textAlign: column.id === 'section' ? 'left' : 'center',
													paddingLeft: column.id === 'section' ? '8px' : '1px',
												}}
											>
												{column.id === 'correctAnswer' ? (
													<Box
														sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
													>
														<Typography variant="body2" color="text.secondary">
															{item.correctAnswer}
														</Typography>
													</Box>
												) : column.id === 'submittedAnswer' ? (
													<Box
														sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
													>
														<Typography
															variant="body2"
															color="text.secondary"
															sx={{ fontStyle: 'italic' }}
														>
															Pending
														</Typography>
													</Box>
												) : column.id === 'picLink' ? (
													<Button
														variant="outlined"
														size="small"
														disabled
														sx={{ textTransform: 'none' }}
													>
														No Picture
													</Button>
												) : (
													<Typography
														variant="body2"
														sx={{
															whiteSpace: 'pre-wrap',
															wordBreak: 'break-word',
														}}
													>
														{item[column.id as keyof ChecklistItem] as string}
													</Typography>
												)}
											</TableCell>
										))}
									</TableRow>
									<TableRow>
										<TableCell colSpan={columns.length} sx={{ p: 1, bgcolor: 'grey.50' }}>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
												<Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '100px' }}>
													Comments:
												</Typography>
												<Typography
													variant="body2"
													sx={{
														whiteSpace: 'pre-wrap',
														wordBreak: 'break-word',
														flexGrow: 1,
														fontStyle: 'italic',
														color: 'text.secondary',
													}}
												>
													No comments yet - checklist pending completion
												</Typography>
											</Box>
										</TableCell>
									</TableRow>
								</React.Fragment>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{!loading && items.length === 0 && (
				<Box sx={{ p: 4, textAlign: 'center' }}>
					<Typography variant="body1" color="text.secondary">
						No items in this checklist.
					</Typography>
				</Box>
			)}
		</Paper>
	);
};

const PendingChecklistView: React.FC<ViewProps> = ({ onBack, selectedItem }) => {
	return (
		<Paper
			elevation={3}
			className={styles.containerr}
			style={{ padding: '20px', backgroundColor: 'whitesmoke' }}
		>
			<Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
				<Button
					variant="outlined"
					onClick={onBack}
					className={styles.button}
					style={{ backgroundColor: 'rgb(68, 99, 136)', padding: '5px 12px', color: 'white' }}
				>
					Back
				</Button>
			</Box>
			<OutstandingChecklistView onBack={onBack} selectedItem={selectedItem} />
		</Paper>
	);
};

// Main Component
const Checklist: React.FC = () => {
	const history = useHistory();
	// history intentionally unused in this component
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const _history = history;
	const [currentView, setCurrentView] = useState<
		'Main' | 'Completed' | 'Approval' | 'Stats' | 'Pending' | 'Toggle'
	>('Main');
	// templates state kept for potential future use
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_templates, _setTemplates] = useState<Template[]>([]);
	const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
	const [selectedOutstandingItem, setSelectedOutstandingItem] =
		useState<ChecklistScheduleEvent | null>(null);
	const [selectedCompletedItem, setSelectedCompletedItem] = useState<ChecklistScheduleEvent | null>(
		null
	);
	const [selectedApprovalItem, setSelectedApprovalItem] = useState<ChecklistScheduleEvent | null>(
		null
	);
	const [scheduledTemplates, setScheduledTemplates] = useState<ScheduledChecklistTemplate[]>([]);
	const [categorizedEvents, setCategorizedEvents] = useState<CategorizedChecklistEvents>({
		completed: [],
		myOutstanding: [],
		waitingForApproval: [],
		pastDue: [],
	});
	const [loading, setLoading] = useState(false);
	const [categorizedLoading, setCategorizedLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [toggleLoading, setToggleLoading] = useState<number | null>(null);
	const [toggleSuccess, setToggleSuccess] = useState<number | null>(null);
	const [existingScheduleData, setExistingScheduleData] = useState<any>(null);
	// loadingScheduleData currently unused but setter is used in dialog flows
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_loadingScheduleData, setLoadingScheduleData] = useState<boolean>(false);
	const setCalenderCheck = useHeaderStore((state) => state.setHeaderTitle);
	const [onTimeStats, setOnTimeStats] = useState<
		Array<{ period: string; onTime: number; signOff: number }>
	>([]);
	const [onTimeLoading, setOnTimeLoading] = useState<boolean>(false);

	// Get date from document context
	const { startDate } = useDocumentContext();

	// State for schedule name dialog
	const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
	const [scheduleDialogTemplate, setScheduleDialogTemplate] = useState<Template | null>(null);
	const [scheduleDialogMessage, setScheduleDialogMessage] = useState<string>('');
	const [isUpdateAvailable, setIsUpdateAvailable] = useState<boolean>(false);
	const [latestTemplate, setLatestTemplate] = useState<any | null>(null);
	const [selectedItemForDialog, setSelectedItemForDialog] =
		useState<ScheduledChecklistTemplate | null>(null);

	// Deactivation confirmation modal state
	const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
	const [pendingDeactivateTemplate, setPendingDeactivateTemplate] =
		useState<ScheduledChecklistTemplate | null>(null);

	// Workplace ID from localStorage (fallback to 1)
	const workplaceId = Number(
		(typeof window !== 'undefined' && window.localStorage
			? localStorage.getItem('workplace-id')
			: null) || 1
	);

	// Helper function to format current date
	const getCurrentDate = () => {
		return new Date().toLocaleDateString('en-US', {
			month: '2-digit',
			day: '2-digit',
			year: 'numeric',
		});
	};

	// Fetch scheduled checklist templates
	const fetchScheduledTemplates = async () => {
		try {
			setLoading(true);
			setError(null);
			const data = await getScheduledChecklistTemplates(1, workplaceId);

			// Just set the templates without making version check API calls
			// Version checking will only happen when user clicks on checklist name in Schedule section
			// console.log(`Fetched ${data.length} scheduled templates without version checking`);
			setScheduledTemplates(data);
		} catch (error) {
			console.error('Error fetching scheduled templates:', error);
			setError('Failed to fetch scheduled templates.');
		} finally {
			setLoading(false);
		}
	};

	// Fetch categorized checklist schedule events
	const fetchCategorizedEvents = async () => {
		if (!startDate) {
			return;
		}

		try {
			setCategorizedLoading(true);
			const data = await getCategorizedChecklistScheduleEvents(startDate, workplaceId);
			setCategorizedEvents(data);
		} catch (error) {
			console.error('Error fetching categorized events:', error);
			// Keep existing data on error
		} finally {
			setCategorizedLoading(false);
		}
	};

	// Fetch data on component mount
	useEffect(() => {
		fetchScheduledTemplates();
		fetchCategorizedEvents();
		(async () => {
			try {
				setOnTimeLoading(true);
				const data = await getOnTimeStats();
				setOnTimeStats(Array.isArray(data) ? data : []);
			} catch (e) {
				setOnTimeStats([]);
			} finally {
				setOnTimeLoading(false);
			}
		})();
		// fetchScheduledTemplates and fetchCategorizedEvents are stable and intentionally
		// excluded from deps to run only on mount.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Refetch categorized events when date changes
	useEffect(() => {
		if (startDate) {
			fetchCategorizedEvents();
		}
		// fetchCategorizedEvents is stable and intentionally excluded from deps.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startDate]);

	// Handler for clicking Schedule Checklist Name (ONLY in Schedule section)
	const handleScheduleChecklistNameClick = async (item: ScheduledChecklistTemplate) => {
		if (!canExecuteChecklistAll) {
			console.log('User does not have Checklist - All Execute permission');
			return;
		}

		console.log(
			`Schedule section: User clicked on checklist "${item.label}" (isScheduled=${item.isScheduled})`
		);

		const template: Template = {
			id: item.id,
			version: (item.version || 1).toString(),
			name: item.label,
			category: item.category?.label || 'Unknown Category',
			owner: getOwnerName(item.type),
			frequency: item.frequency?.label || 'Unknown Frequency',
			frequencyObject: item.frequency,
			assigned: item.author.firstName + ' ' + item.author.lastName,
			active: Boolean(item.isScheduled),
			signoffRequired: item.signoffRequired,
			templateId: item.id,
			templateUid: item.templateUid,
		};

		// Only make version check API call if isScheduled is true
		const isScheduledValue =
			Boolean(item.isScheduled) &&
			(item.isScheduled === true ||
				String(item.isScheduled).toLowerCase() === 'true' ||
				Number(item.isScheduled) === 1);

		if (item.templateUid && isScheduledValue) {
			setSelectedItemForDialog(item);
			setScheduleDialogTemplate(template);

			try {
				const latest = await getLatestChecklistTemplateByUid(item.templateUid);

				setLatestTemplate(latest);
				const currentVersion = Number(item.version || 1);
				const latestVersion = Number(latest?.version || currentVersion);

				const updateAvailable = latestVersion > currentVersion;

				if (updateAvailable) {
					setIsUpdateAvailable(updateAvailable);
					setScheduleDialogMessage(
						'This schedule is not using the latest version of this template. Would you like to update to the latest version?'
					);
				} else {
					const scheduleData = await getChecklistScheduleById(item.checklistScheduleId);
					setExistingScheduleData(scheduleData);

					const finalTemplateId = Number(item.id);
					const finalVersion = Number(item.version || 1);

					setSelectedTemplate({
						id: finalTemplateId,
						version: finalVersion.toString(),
						name: template.name,
						category: template.category,
						owner: template.owner,
						frequency: template.frequency,
						frequencyObject: template.frequencyObject,
						assigned: template.assigned,
						active: template.active,
						templateId: finalTemplateId,
						templateUid: template.templateUid,
					});

					setCurrentView('Toggle');
					return;
				}
			} catch (e) {
				setIsUpdateAvailable(false);
				setLatestTemplate(null);
				setScheduleDialogMessage(
					'Unable to verify latest version right now. Continue with current version?'
				);
			}
		} else {
			const templateForActivation: Template = {
				id: item.id,
				version: (item.version || 1).toString(),
				name: item.label,
				category: item.category?.label || 'Unknown Category',
				owner: getOwnerName(item.type),
				frequency: item.frequency?.label || 'Unknown Frequency',
				frequencyObject: item.frequency,
				assigned: item.author.firstName + ' ' + item.author.lastName,
				active: Boolean(item.isScheduled),
				signoffRequired: item.signoffRequired,
				templateId: item.id,
				templateUid: item.templateUid,
			};

			setSelectedTemplate(templateForActivation);
			setCurrentView('Toggle');
			return;
		}

		setScheduleDialogOpen(true);
	};

	const handleScheduleDialogClose = () => {
		setScheduleDialogOpen(false);
		setScheduleDialogTemplate(null);
		setSelectedItemForDialog(null);
		setLatestTemplate(null);
		setScheduleDialogMessage('');
		setIsUpdateAvailable(false);
		setLoadingScheduleData(false);
	};

	const clearExistingScheduleData = () => {
		setExistingScheduleData(null);
	};

	const handleScheduleDialogConfirm = async () => {
		if (!scheduleDialogTemplate || !selectedItemForDialog) {
			handleScheduleDialogClose();
			return;
		}

		try {
			setLoadingScheduleData(true);

			// Determine which template/version to use
			const useLatest = isUpdateAvailable && latestTemplate;

			if (useLatest) {
				const finalTemplateId = Number(latestTemplate.id);
				const finalVersion = Number(latestTemplate.version);
				const finalLabel = latestTemplate.label || scheduleDialogTemplate.name;
				const finalCategory = latestTemplate?.category?.label || scheduleDialogTemplate.category;
				const finalFrequency = latestTemplate?.frequency?.label || scheduleDialogTemplate.frequency;
				const finalFrequencyObject = latestTemplate?.frequency;
				const finalOwner = getOwnerName(latestTemplate?.type) || scheduleDialogTemplate.owner;

				setSelectedTemplate({
					id: finalTemplateId,
					version: finalVersion.toString(),
					name: finalLabel,
					category: finalCategory,
					owner: finalOwner,
					frequency: finalFrequency,
					frequencyObject: finalFrequencyObject,
					assigned: scheduleDialogTemplate.assigned,
					active: scheduleDialogTemplate.active,
					templateId: finalTemplateId,
					templateUid: scheduleDialogTemplate.templateUid,
				});
				// Ensure PUT is used after YES by providing schedule id from listing response
				if (selectedItemForDialog?.checklistScheduleId) {
					setExistingScheduleData({ id: selectedItemForDialog.checklistScheduleId });
				} else {
					setExistingScheduleData(null);
				}
			} else {
				const scheduleData = await getChecklistScheduleById(
					selectedItemForDialog.checklistScheduleId
				);
				setExistingScheduleData(scheduleData);

				const finalTemplateId = Number(selectedItemForDialog.id);
				const finalVersion = Number(selectedItemForDialog.version || 1);

				setSelectedTemplate({
					id: finalTemplateId,
					version: finalVersion.toString(),
					name: scheduleDialogTemplate.name,
					category: scheduleDialogTemplate.category,
					owner: scheduleDialogTemplate.owner,
					frequency: scheduleDialogTemplate.frequency,
					frequencyObject: scheduleDialogTemplate.frequencyObject,
					assigned: scheduleDialogTemplate.assigned,
					active: scheduleDialogTemplate.active,
					templateId: finalTemplateId,
					templateUid: scheduleDialogTemplate.templateUid,
				});
			}

			setCurrentView('Toggle');
			handleScheduleDialogClose();
		} catch (error) {
			console.error('Error in handleScheduleDialogConfirm:', error);
			setCurrentView('Toggle');
			handleScheduleDialogClose();
		} finally {
			setLoadingScheduleData(false);
		}
	};

	const handleScheduleDialogNo = async () => {
		if (!scheduleDialogTemplate || !selectedItemForDialog) {
			handleScheduleDialogClose();
			return;
		}

		try {
			setLoadingScheduleData(true);

			const scheduleData = await getChecklistScheduleById(
				selectedItemForDialog.checklistScheduleId
			);
			setExistingScheduleData(scheduleData);

			const finalTemplateId = Number(selectedItemForDialog.id);
			const finalVersion = Number(selectedItemForDialog.version || 1);

			setSelectedTemplate({
				id: finalTemplateId,
				version: finalVersion.toString(),
				name: scheduleDialogTemplate.name,
				category: scheduleDialogTemplate.category,
				owner: scheduleDialogTemplate.owner,
				frequency: scheduleDialogTemplate.frequency,
				frequencyObject: scheduleDialogTemplate.frequencyObject,
				assigned: scheduleDialogTemplate.assigned,
				active: scheduleDialogTemplate.active,
				templateId: finalTemplateId,
				templateUid: scheduleDialogTemplate.templateUid,
			});

			setCurrentView('Toggle');
			handleScheduleDialogClose();
		} catch (error) {
			console.error('Error fetching existing schedule data:', error);

			const finalTemplateId = Number(selectedItemForDialog.id);
			const finalVersion = Number(selectedItemForDialog.version || 1);

			setSelectedTemplate({
				id: finalTemplateId,
				version: finalVersion.toString(),
				name: scheduleDialogTemplate.name,
				category: scheduleDialogTemplate.category,
				owner: scheduleDialogTemplate.owner,
				frequency: scheduleDialogTemplate.frequency,
				frequencyObject: scheduleDialogTemplate.frequencyObject,
				assigned: scheduleDialogTemplate.assigned,
				active: scheduleDialogTemplate.active,
				templateId: finalTemplateId,
				templateUid: scheduleDialogTemplate.templateUid,
			});

			setCurrentView('Toggle');
			handleScheduleDialogClose();
		} finally {
			setLoadingScheduleData(false);
		}
	};

	// Set headerText in localStorage based on currentView
	useEffect(() => {
		try {
			if (currentView === 'Main') {
				setCalenderCheck(true);
			} else if (['Completed', 'Approval', 'Toggle', 'Pending', 'Stats'].includes(currentView)) {
				setCalenderCheck(false);
			}
		} catch (e) {
			console.error('localStorage not available', e);
		}
		// setCalenderCheck is stable and intentionally excluded from dependencies.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentView]);

	const handleChecklistClick = async (
		view: 'Completed' | 'Approval' | 'Stats' | 'Pending',
		itemData?: ChecklistScheduleEvent
	): Promise<void> => {
		console.log(
			`✅ ${view} section: User clicked on checklist "${itemData?.checklistTemplate?.label || 'Unknown'}" - Fetching detailed template data`
		);

		if (!itemData?.checklistTemplate?.id) {
			console.error('No checklist template ID found');
			return;
		}

		try {
			setLoading(true);

			// Fetch detailed template data using the template ID
			const detailedTemplate = await getChecklistTemplateById(itemData.checklistTemplate.id);

			// Update the item data with the detailed template information
			const updatedItemData = {
				...itemData,
				checklistTemplate: {
					...itemData.checklistTemplate,
					...detailedTemplate,
					tasks: detailedTemplate.tasks || [],
				},
			};

			console.log('Detailed template data fetched:', detailedTemplate);

			setCurrentView(view);

			if (view === 'Pending') {
				setSelectedOutstandingItem(updatedItemData);
			} else if (view === 'Completed') {
				setSelectedCompletedItem(updatedItemData);
			} else if (view === 'Approval') {
				setSelectedApprovalItem(updatedItemData);
			}
		} catch (error) {
			console.error('Error fetching detailed template data:', error);
			// Still navigate to the view even if API call fails
			setCurrentView(view);
			if (itemData) {
				if (view === 'Pending') {
					setSelectedOutstandingItem(itemData);
				} else if (view === 'Completed') {
					setSelectedCompletedItem(itemData);
				} else if (view === 'Approval') {
					setSelectedApprovalItem(itemData);
				}
			}
		} finally {
			setLoading(false);
		}
	};

	const handleBack = (): void => {
		setCurrentView('Main');
		setSelectedTemplate(null);
		setSelectedOutstandingItem(null);
		setSelectedCompletedItem(null);
		setSelectedApprovalItem(null);
		clearExistingScheduleData();
		fetchScheduledTemplates();
	};

	const getOwnerName = (type?: string) => {
		if (type === 'workplace') return 'Workplace';
		if (type === 'organization') return 'Organization';
		return '-';
	};

	// Permission check: require 'Checklist - My' view permission to show any section data
	const { hasModuleViewPermission, hasModuleEditPermission, hasModuleExecutePermission } = usePermissionCheck();
	// Use module-level permissions (group 4) for Checklist views
	const canViewChecklistMy = hasModuleViewPermission('Checklist - My');
	const canViewChecklistAll = hasModuleViewPermission('Checklist - All');
	const canExecuteChecklistAll = hasModuleExecutePermission('Checklist - All');
	// console.log('checklist permissions:', { canViewChecklistMy, canViewChecklistAll, canExecuteChecklistAll });
		// Determine edit capability based on view permissions:
		// - If user can view 'Checklist - All' then check module-level edit for 'Checklist' OR edit on 'Checklist - All' OR 'Checklist - My'
		// - If user can only view 'Checklist - My' then check module-level edit for 'Checklist' OR edit on 'Checklist - My'
		let canEditChecklist = false;
		if (canViewChecklistAll) {
			canEditChecklist =
				hasModuleEditPermission('Checklist - All') || hasModuleEditPermission('Checklist - My');
		} else if (canViewChecklistMy) {
			canEditChecklist = hasModuleEditPermission('Checklist - My');
      // console.log("has edit for checklist my", canEditChecklist);
		} else {
			canEditChecklist = false;
		}
		// console.log('has permissions to edit', canEditChecklist);

	// Determine current user id from localStorage (BYOD-app or userId fallback)
	const getCurrentUserId = (): number | null => {
		try {
			const app =
				typeof window !== 'undefined' && localStorage.getItem('BYOD-app')
					? JSON.parse(localStorage.getItem('BYOD-app') as string)
					: null;
			const idFromApp =
				app && app.user && (app.user.id || app.user['@id']) ? app.user.id || app.user['@id'] : null;
			if (idFromApp) return Number(idFromApp);
			const idFromLS = localStorage.getItem('userId');
			if (idFromLS) return Number(idFromLS);
		} catch (e) {
			// ignore
		}
		return null;
	};

	const currentUserId = getCurrentUserId();

	// Helper to extract an id from completedBy payload. Supports completedBy['@id'], completedBy.id or string-formatted ids like 'user:123'.
	const getCompletedById = (completedBy: any): number | null => {
		if (!completedBy) return null;
		if (typeof completedBy === 'object') {
			if (completedBy['@id']) {
				const raw = String(completedBy['@id']);
				const match = raw.match(/(\d+)$/);
				if (match) return Number(match[1]);
				const num = Number(raw);
				return Number.isNaN(num) ? null : num;
			}
			if (completedBy.id) return Number(completedBy.id) || null;
		}
		if (typeof completedBy === 'string') {
			const match = completedBy.match(/(\d+)$/);
			if (match) return Number(match[1]);
		}
		return null;
	};

	// Helper to determine if an event is assigned to current user (for myOutstanding)
	const isAssignedToCurrentUser = (item: ChecklistScheduleEvent): boolean => {
		try {
			// If assignment type is group, trust that the API has already filtered this correctly
			// The myOutstanding array from the API only contains items for the current user
			if (item.checklistSchedule?.assignmentType === 'group') {
				return true;
			}
			
			const assigneeId = item.checklistSchedule?.assignee?.id;
			if (assigneeId) return Number(assigneeId) === Number(currentUserId);
			// fallback: check completedBy for same id (some payloads use completedBy for ownership)
			const completedById = getCompletedById(item.completedBy);
			if (completedById) return Number(completedById) === Number(currentUserId);
		} catch (e) {
			// ignore
		}
		return false;
	};

	// Compute filtered lists according to permissions and ownership
	const completedToShow: ChecklistScheduleEvent[] = canViewChecklistAll
		? categorizedEvents.completed
		: canViewChecklistMy
			? categorizedEvents.completed.filter(
					(ev) => getCompletedById(ev.completedBy) === currentUserId
				)
			: [];

	const approvalToShow: ChecklistScheduleEvent[] = canViewChecklistAll
		? categorizedEvents.waitingForApproval
		: canViewChecklistMy
			? categorizedEvents.waitingForApproval.filter(
					(ev) => getCompletedById(ev.completedBy) === currentUserId
				)
			: [];

	// My outstanding should show only items assigned to current user
	const myOutstandingToShow: ChecklistScheduleEvent[] = categorizedEvents.myOutstanding.filter(
		(ev) => isAssignedToCurrentUser(ev)
	);

	const pastDueToShow: ChecklistScheduleEvent[] = canViewChecklistAll
		? categorizedEvents.pastDue
		: canViewChecklistMy
			? categorizedEvents.pastDue.filter((ev) => isAssignedToCurrentUser(ev))
			: [];

	// Helper to extract author id from scheduled template
	const getAuthorId = (author: any): number | null => {
		if (!author) return null;
		if (typeof author === 'object') {
			if (author.id) return Number(author.id) || null;
			if (author['@id']) {
				const raw = String(author['@id']);
				const match = raw.match(/(\d+)$/);
				if (match) return Number(match[1]);
			}
		}
		if (typeof author === 'string') {
			const match = author.match(/(\d+)$/);
			if (match) return Number(match[1]);
		}
		return null;
	};

	// For Schedule Checklists: show all if user has Checklist - All; if only Checklist - My, show schedules created by the user (author.id)
	const scheduledToShow = canViewChecklistAll
		? scheduledTemplates
		: canViewChecklistMy
			? scheduledTemplates.filter((tpl) => getAuthorId(tpl.author) === currentUserId)
			: [];

	const getAssignedDisplay = (item: ChecklistScheduleEvent) => {
		// If assignment type is group, show group label
		if (item.checklistSchedule.assignmentType === 'group') {
			return item.checklistSchedule.group?.label || 'Unassigned Group';
		}

		// If assignment type is assignee, show assignee's firstName and lastName
		if (item.checklistSchedule.assignmentType === 'assignee') {
			return item.checklistSchedule.assignee
				? `${item.checklistSchedule.assignee.firstName} ${item.checklistSchedule.assignee.lastName}`.trim()
				: 'Unassigned';
		}

		// Fallback for any other assignment type
		return 'Unassigned';
	};

	const handleToggle = async (id: number): Promise<void> => {
		if (!canExecuteChecklistAll) {
			console.log('User does not have Checklist - All Execute permission');
			return;
		}

		// Find the template to get its checklistScheduleId
		const template = scheduledTemplates.find((t) => t.id === id);
		if (!template) {
			console.error('Template not found for ID:', id);
			return;
		}

		const newActiveState = !template.isScheduled;

		if (!newActiveState && template.checklistScheduleId) {
			// Toggle is being turned OFF - show confirmation modal
			setPendingDeactivateTemplate(template);
			setDeactivateConfirmOpen(true);
			return;
		}

		// If activating (turning ON), proceed directly
		await performToggle(id, newActiveState);
	};

	const performToggle = async (id: number, newActiveState: boolean): Promise<void> => {
		setToggleLoading(id);

		try {
			// Find the template to get its checklistScheduleId
			const template = scheduledTemplates.find((t) => t.id === id);
			if (!template) {
				console.error('Template not found for ID:', id);
				setToggleLoading(null);
				return;
			}

			if (!newActiveState && template.checklistScheduleId) {
				// Toggle is being turned OFF - call deactivate API
				await deactivateChecklistSchedule(template.checklistScheduleId);
			}

			// Update the scheduledTemplates state
			setScheduledTemplates((prevTemplates) =>
				prevTemplates.map((template) => {
					if (template.id === id) {
						if (newActiveState) {
							// Create a Template object from ScheduledChecklistTemplate for compatibility
							const templateForToggle: Template = {
								id: template.id,
								version: template.version.toString(),
								name: template.label,
								category: template.category?.label || 'Unknown Category',
								owner: getOwnerName(template.type),
								frequency: template.frequency?.label || 'Unknown Frequency',
								frequencyObject: template.frequency,
								assigned: template.author.firstName + ' ' + template.author.lastName,
								active: newActiveState,
								signoffRequired: template.signoffRequired,
							};
							setSelectedTemplate(templateForToggle);
							setCurrentView('Toggle');
						}
						return { ...template, isScheduled: newActiveState as boolean };
					}
					return template;
				})
			);

			setToggleLoading(null);
			setToggleSuccess(id);
			// Clear success message after 2 seconds
			setTimeout(() => setToggleSuccess(null), 2000);
		} catch (error) {
			console.error('Error toggling checklist schedule:', error);
			setToggleLoading(null);
			// Optionally show error toast
		}
	};

	// Handle deactivation confirmation
	const handleDeactivateConfirm = async () => {
		if (pendingDeactivateTemplate) {
			await performToggle(pendingDeactivateTemplate.id, false);
		}
		setDeactivateConfirmOpen(false);
		setPendingDeactivateTemplate(null);
	};

	// Handle deactivation cancellation
	const handleDeactivateCancel = () => {
		setDeactivateConfirmOpen(false);
		setPendingDeactivateTemplate(null);
	};

	if (currentView === 'Completed') {
		if (!(canViewChecklistAll || canViewChecklistMy)) {
			return (
				<Paper
					elevation={3}
					className={styles.containerr}
					style={{ padding: '20px', backgroundColor: 'whitesmoke' }}
				>
					<Box display="flex" justifyContent="center" alignItems="center" mb={2} sx={{ p: 4 }}>
						<Typography variant="body1" color="text.secondary">
							you do not have permissions to view this!
						</Typography>
					</Box>
				</Paper>
			);
		}

		return <CompletedChecklistView onBack={handleBack} selectedItem={selectedCompletedItem} />;
	}

	if (currentView === 'Approval') {
		if (!(canViewChecklistAll || canViewChecklistMy)) {
			return (
				<Paper
					elevation={3}
					className={styles.containerr}
					style={{ padding: '20px', backgroundColor: 'whitesmoke' }}
				>
					<Box display="flex" justifyContent="center" alignItems="center" mb={2} sx={{ p: 4 }}>
						<Typography variant="body1" color="text.secondary">
							you do not have permissions to view this!
						</Typography>
					</Box>
				</Paper>
			);
		}

		return <ApprovalChecklistView onBack={handleBack} selectedItem={selectedApprovalItem} onRefresh={fetchCategorizedEvents} />;
	}

	if (currentView === 'Stats') {
		if (!canViewChecklistAll || !canViewChecklistMy) {
			return (
				<Paper
					elevation={3}
					className={styles.containerr}
					style={{ padding: '20px', backgroundColor: 'whitesmoke' }}
				>
					<Box display="flex" justifyContent="center" alignItems="center" mb={2} sx={{ p: 4 }}>
						<Typography variant="body1" color="text.secondary">
							you do not have permissions to view this!
						</Typography>
					</Box>
				</Paper>
			);
		}

		return <StatsChecklistView onBack={handleBack} />;
	}

	if (currentView === 'Pending') {
		if (!(canViewChecklistMy || canViewChecklistAll)) {
			return (
				<Paper
					elevation={3}
					className={styles.containerr}
					style={{ padding: '20px', backgroundColor: 'whitesmoke' }}
				>
					<Box display="flex" justifyContent="center" alignItems="center" mb={2} sx={{ p: 4 }}>
						<Typography variant="body1" color="text.secondary">
							You do not have permissions to view this!
						</Typography>
					</Box>
				</Paper>
			);
		}

		return <PendingChecklistView onBack={handleBack} selectedItem={selectedOutstandingItem} />;
	}

	if (currentView === 'Toggle' && selectedTemplate) {
		// Debug selectedTemplate data
		// console.log('🔍 SelectedTemplate Debug:', {
		// 	selectedTemplate,
		// 	frequency: selectedTemplate.frequency,
		// 	frequencyObject: selectedTemplate.frequencyObject,
		// });
		if (!(canViewChecklistAll || canViewChecklistMy)) {
			return (
				<Paper
					elevation={3}
					className={styles.containerr}
					style={{ padding: '20px', backgroundColor: 'whitesmoke' }}
				>
					<Box display="flex" justifyContent="center" alignItems="center" mb={2} sx={{ p: 4 }}>
						<Typography variant="body1" color="text.secondary">
							you do not have permissions to view this!
						</Typography>
					</Box>
				</Paper>
			);
		}

		return (
			<ToggleViews
				onBack={handleBack}
				headerData={[
					{
						version: selectedTemplate.version,
						checklistName: selectedTemplate.name,
						category: selectedTemplate.category,
						owner: selectedTemplate.owner,
						frequency: selectedTemplate.frequency || 'Daily',
						frequencyObject: selectedTemplate.frequencyObject,
						date: getCurrentDate(),
						signoffRequired: selectedTemplate.signoffRequired || false,
						templateId: selectedTemplate.templateId || selectedTemplate.id,
					},
				]}
				existingScheduleData={existingScheduleData}
			/>
		);
	}

	// Show full page loading state on initial load
	if (loading && scheduledTemplates.length === 0) {
		return (
			<div className={styles.fullPageLoading}>
				<div className={styles.fullPageLoadingContent}>
					<CircularProgress size={24} className={styles.loadingSpinner} />
				</div>
			</div>
		);
	}

	return (
		<div className={styles.checklist_page}>
			<div className={styles.cardContainer}>
				<TableContainer className={styles.cardCompleted} component={Paper} sx={{ mb: 2 }}>
					<Typography variant="h6" sx={{ p: 2, m: 0 }}>
						Completed
					</Typography>
					<div className={styles.tableContainer}>
						<Table stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Checklist Name
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Assigned
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Owner
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Completed Time
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!canViewChecklistAll && !canViewChecklistMy ? (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
											<div className={styles.emptyState}>
												You do not have permissions to view this!
											</div>
										</TableCell>
									</TableRow>
								) : categorizedLoading ? (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
											<CircularProgress size={20} className={styles.loadingSpinner} />
										</TableCell>
									</TableRow>
								) : completedToShow.length === 0 ? (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
											<div className={styles.emptyState}>No completed checklists</div>
										</TableCell>
									</TableRow>
								) : (
									completedToShow.map((item) => (
										<TableRow
											key={item.id}
											sx={{
												'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', cursor: 'pointer' },
											}}
										>
											<TableCell onClick={() => handleChecklistClick('Completed', item)}>
												{item.checklistTemplate?.label || 'Unknown Template'}
											</TableCell>
											<TableCell>{getAssignedDisplay(item)}</TableCell>
											<TableCell>{getOwnerName(item.checklistTemplate?.type)}</TableCell>
											<TableCell>
												{item.completedAt ? formatDateTime(item.completedAt) : 'N/A'}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</div>
				</TableContainer>

				<TableContainer className={styles.cardApproval} component={Paper} sx={{ mb: 2 }}>
					<Typography variant="h6" sx={{ p: 2, m: 0 }}>
						Approval Required
					</Typography>
					<div className={styles.tableContainer}>
						<Table stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Checklist Name
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Assigned
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Owner
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Completed Time
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!canViewChecklistAll && !canViewChecklistMy ? (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
											<div className={styles.emptyState}>
												You do not have permissions to view this!
											</div>
										</TableCell>
									</TableRow>
								) : categorizedLoading ? (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
											<CircularProgress size={20} className={styles.loadingSpinner} />
										</TableCell>
									</TableRow>
								) : approvalToShow.length === 0 ? (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
											<div className={styles.emptyState}>No checklists waiting for approval</div>
										</TableCell>
									</TableRow>
								) : (
									approvalToShow.map((item) => (
										<TableRow
											key={item.id}
											sx={{
												'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', cursor: 'pointer' },
											}}
										>
											<TableCell onClick={() => handleChecklistClick('Approval', item)}>
												{item.checklistTemplate?.label || 'Unknown Template'}
											</TableCell>
											<TableCell>{getAssignedDisplay(item)}</TableCell>
											<TableCell>{getOwnerName(item.checklistTemplate?.type)}</TableCell>
											<TableCell>
												{item.completedAt ? formatDateTime(item.completedAt) : 'N/A'}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</div>
				</TableContainer>

				<TableContainer className={styles.cardStats} component={Paper} sx={{ mb: 2 }}>
					<Typography variant="h6" sx={{ p: 2, m: 0 }}>
						On Time Stats
					</Typography>
					<div className={styles.tableContainer}>
						<Table stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										On Time
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Sign Off
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!canViewChecklistAll ? (
									<TableRow>
										<TableCell colSpan={3} align="center">
											<div className={styles.emptyState}>
												You do not have permissions to view this!
											</div>
										</TableCell>
									</TableRow>
								) : onTimeLoading ? (
									<TableRow>
										<TableCell colSpan={3} align="center">
											<CircularProgress size={24} className={styles.loadingSpinner} />
										</TableCell>
									</TableRow>
								) : (
									['WTD', 'PTD', 'YTD'].map((period) => {
										const found = onTimeStats.find((s) => s.period === period);
										const onTime = found ? `${Number(found.onTime || 0).toFixed(1)}%` : '0%';
										const signOff = found ? `${Number(found.signOff || 0).toFixed(1)}%` : '0%';
										return (
											<TableRow
												key={period}
												sx={{
													'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', cursor: 'pointer' },
												}}
											>
												<TableCell>{period}</TableCell>
												<TableCell>{onTime}</TableCell>
												<TableCell>{signOff}</TableCell>
											</TableRow>
										);
									})
								)}
							</TableBody>
						</Table>
					</div>
				</TableContainer>
			</div>

			<div className={styles.largeCardsContainer}>
				<div className={styles.largeCard}>
					<Typography variant="h6" sx={{ p: 2, m: 0 }}>
						My Outstanding Checklists
					</Typography>
					<div className={styles.largeCardTableContainer}>
						<Table stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Checklist Name
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Assigned
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Owner
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Checklist Due
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{categorizedLoading ? (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
											<CircularProgress size={20} className={styles.loadingSpinner} />
										</TableCell>
									</TableRow>
								) : myOutstandingToShow.length === 0 ? (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
											<div className={styles.emptyState}>No outstanding checklists</div>
										</TableCell>
									</TableRow>
								) : (
									myOutstandingToShow.map((item) => (
										<TableRow
											key={item.id}
											sx={{
												'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', cursor: 'pointer' },
											}}
										>
											<TableCell onClick={() => handleChecklistClick('Pending', item)}>
												{item.checklistTemplate?.label || 'Unknown Template'}
											</TableCell>
											<TableCell>{getAssignedDisplay(item)}</TableCell>
											<TableCell>{getOwnerName(item.checklistTemplate?.type)}</TableCell>
											<TableCell>
												{item.scheduledDateFormatted && item.scheduledTimeFormatted
													? formatDateTimeFromAPI(
															item.scheduledDateFormatted,
															item.scheduledTimeFormatted
														)
													: formatDateTime(item.scheduledDate)}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</div>
				</div>
				<div className={styles.largeCard}>
					<Typography variant="h6" sx={{ p: 2, m: 0 }}>
						Past Due Checklists
					</Typography>
					<div className={styles.largeCardTableContainer}>
						<Table stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Checklist Name
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Assigned
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Owner
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										Checklist Due
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!canViewChecklistAll && !canViewChecklistMy ? (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
											<div className={styles.emptyState}>
												You do not have permissions to view this!
											</div>
										</TableCell>
									</TableRow>
								) : categorizedLoading ? (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
											<CircularProgress size={20} className={styles.loadingSpinner} />
										</TableCell>
									</TableRow>
								) : pastDueToShow.length === 0 ? (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 6 }}>
											<div className={styles.emptyState}>No past due checklists</div>
										</TableCell>
									</TableRow>
								) : (
									pastDueToShow.map((item) => (
										<TableRow
											key={item.id}
											sx={{
												'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', cursor: 'pointer' },
											}}
										>
											<TableCell onClick={() => handleChecklistClick('Pending', item)}>
												{item.checklistTemplate?.label || 'Unknown Template'}
											</TableCell>
											<TableCell>{getAssignedDisplay(item)}</TableCell>
											<TableCell>{getOwnerName(item.checklistTemplate?.type)}</TableCell>
											<TableCell
												style={{
													display: 'flex',
													gap: '10px',
													justifyContent: 'space-between',
													alignItems: 'center',
												}}
											>
												<span>
													{item.scheduledDateFormatted && item.scheduledTimeFormatted
														? formatDateTimeFromAPI(
																item.scheduledDateFormatted,
																item.scheduledTimeFormatted
															)
														: formatDateTime(item.scheduledDate)}
												</span>
												<img src={PastDueLogo} alt="past due" height="30px" />
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</div>
				</div>
			</div>

			<div className={styles.templateContainer}>
				<div className={styles.templateSection}>
					<Typography variant="h6" sx={{ p: 2, m: 0 }}>
						Schedule Checklists
					</Typography>
					<div className={styles.templateTableContainer}>
						<Table stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell
										sx={{
											fontWeight: 'bold',
											backgroundColor: 'rgb(245, 245, 245)',
											color: '#000',
										}}
									>
										Name
									</TableCell>
									<TableCell
										sx={{
											fontWeight: 'bold',
											backgroundColor: 'rgb(245, 245, 245)',
											color: '#000',
										}}
									>
										Category
									</TableCell>
									<TableCell
										sx={{
											fontWeight: 'bold',
											backgroundColor: 'rgb(245, 245, 245)',
											color: '#000',
										}}
									>
										Owner
									</TableCell>
									<TableCell
										sx={{
											fontWeight: 'bold',
											backgroundColor: 'rgb(245, 245, 245)',
											color: '#000',
										}}
									>
										Frequency
									</TableCell>
									<TableCell
										sx={{
											fontWeight: 'bold',
											backgroundColor: 'rgb(245, 245, 245)',
											color: '#000',
										}}
									>
										Version
									</TableCell>
									<TableCell
										sx={{
											fontWeight: 'bold',
											backgroundColor: 'rgb(245, 245, 245)',
											color: '#000',
										}}
									>
										Signoff Required
									</TableCell>
									<TableCell
										sx={{
											fontWeight: 'bold',
											backgroundColor: 'rgb(245, 245, 245)',
											color: '#000',
										}}
									>
										Status
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!canViewChecklistAll && !canViewChecklistMy ? (
									<TableRow>
										<TableCell colSpan={7} align="center" sx={{ py: 6 }}>
											<div className={styles.emptyState}>
												You do not have permissions to view this!
											</div>
										</TableCell>
									</TableRow>
								) : loading ? (
									<TableRow>
										<TableCell colSpan={7} align="center" sx={{ py: 6 }}>
											<CircularProgress size={20} className={styles.loadingSpinner} />
										</TableCell>
									</TableRow>
								) : error ? (
									<TableRow>
										<TableCell colSpan={7} align="center" sx={{ py: 6 }}>
											<div className={styles.errorState}>{error}</div>
										</TableCell>
									</TableRow>
								) : scheduledToShow.length === 0 ? (
									<TableRow>
										<TableCell colSpan={7} align="center" sx={{ py: 6 }}>
											<div className={styles.emptyState}>No scheduled templates found</div>
										</TableCell>
									</TableRow>
								) : (
									scheduledToShow.map((item) => (
										<TableRow
											key={item.id}
											sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
										>
											<TableCell
												style={{ cursor: canExecuteChecklistAll ? 'pointer' : 'default' }}
												onClick={canExecuteChecklistAll ? () => handleScheduleChecklistNameClick(item) : undefined}
											>
												{item.label}
											</TableCell>
											<TableCell>{item.category?.label || 'Unknown Category'}</TableCell>
											<TableCell>{getOwnerName(item.type)}</TableCell>
											<TableCell>{item.frequency?.label || 'Unknown Frequency'}</TableCell>
											<TableCell>{item.version}</TableCell>
											<TableCell>{item.signoffRequired ? 'Yes' : 'No'}</TableCell>
											<TableCell>
												<button
													className={`${styles.toggleButton} ${item.isScheduled ? styles.on : styles.off} ${toggleSuccess === item.id ? styles.success : ''}`}
													onClick={() => handleToggle(item.id)}
													title={item.isScheduled ? 'Scheduled' : 'Not Scheduled'}
													disabled={toggleLoading === item.id || !canExecuteChecklistAll}
													aria-disabled={toggleLoading === item.id || !canExecuteChecklistAll}
													style={{
														cursor:
															toggleLoading === item.id || !canExecuteChecklistAll
																? 'not-allowed'
																: 'pointer',
													}}
												>
													{toggleLoading === item.id ? (
														<span style={{ fontSize: '10px', color: 'white' }}>...</span>
													) : toggleSuccess === item.id ? (
														<span style={{ fontSize: '12px', color: 'white' }}>✓</span>
													) : (
														<span className={styles.knob}></span>
													)}
												</button>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</div>
				</div>
			</div>

			{/* Dialog for Schedule Checklist Name click */}
			<Dialog open={scheduleDialogOpen} onClose={handleScheduleDialogClose} maxWidth="xs" fullWidth>
				<DialogTitle>Update Schedule</DialogTitle>
				<DialogContent>
					<span>{scheduleDialogMessage || 'Would you like to proceed?'}</span>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleScheduleDialogNo} color="secondary" variant="outlined">
						No
					</Button>
					<Button
						onClick={handleScheduleDialogConfirm}
						className={styles.backButton}
						variant="outlined"
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>

			{/* Deactivation Confirmation Dialog */}
			<Dialog open={deactivateConfirmOpen} onClose={handleDeactivateCancel} maxWidth="sm" fullWidth>
				<DialogTitle>Confirm Deactivation</DialogTitle>
				<DialogContent>
					<p>
						Are you sure you want to deactivate the schedule{' '}
						<strong>&ldquo;{pendingDeactivateTemplate?.label}&rdquo;</strong>? This action cannot be
						undone.
					</p>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleDeactivateCancel}
						color="secondary"
						variant="outlined"
						style={{ color: 'black' }}
					>
						Cancel
					</Button>
					<Button
						onClick={handleDeactivateConfirm}
						variant="contained"
						style={{
							backgroundColor: 'red',
							color: 'white',
						}}
					>
						Deactivate
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default Checklist;
